//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import React, { useEffect, useState } from "react";

import { Dropdown } from "semantic-ui-react";
import { Field, FieldContent as FC, FieldLabelWide as FL } from "../../../ui/lib";

import { useDeploymentStore } from "../../../util/storage/DeploymentStore";

import apiclients from "../../../api/apiclients";
import IConfigurationViewItemProps from "./IConfigurationViewItemProps";
import { RenderGuard } from "../../../util/RenderGuard";

type CpuArchType = "amd64" | "arm64";

export const ConfigureArchitectureTypeView = ({ requireComment, requirePermission }: IConfigurationViewItemProps) => {
  const { deployment, deploymentInfo } = useDeploymentStore();
  const { idashboardClient } = apiclients;
  const [architectureType, setArchitectureType] = useState<CpuArchType>("amd64");

  useEffect(() => {
    const persisted = deploymentInfo?.target_cpu_architecture;
    setArchitectureType(persisted === "arm64" ? "arm64" : "amd64");
  }, [deployment.id, deploymentInfo?.target_cpu_architecture]);

  const onApplyArchitectureType = (arch: CpuArchType) => {
    if (arch === architectureType) {
      return;
    }
    requireComment({
      defaultComment: `Set deployment architecture type to ${arch}`,
      loadingMessage: "Applying architecture choice, please wait...",
      onCommentConfirmation: async () => {
        await idashboardClient.UpdateDeploymentTargetCpuArchitecture({
          deployment_id: deployment.id,
          target_cpu_architecture: arch,
        });
        setArchitectureType(arch);
      },
    });
  };

  return (
    <Field>
      <FL>Configure Architecture Type</FL>
      <FC>
        <span>{architectureType}</span>
        <RenderGuard renderIf={requirePermission("data.deployment.update")}>
          <Dropdown icon="pencil" inline className="icon tiny edit-pencil">
            <Dropdown.Menu>
              <Dropdown.Item key="amd64" text="amd64" onClick={() => onApplyArchitectureType("amd64")} />
              <Dropdown.Item key="arm64" text="arm64" onClick={() => onApplyArchitectureType("arm64")} />
            </Dropdown.Menu>
          </Dropdown>
        </RenderGuard>
      </FC>
    </Field>
  );
};
